import { useCallback, useEffect, useMemo, useState } from "react";
import { useDebounce } from "@hopper-b2b/utilities";
import { useControlledDialog } from "@tenant/ui";
import type {
  LocationAutocompleteProps,
  LocationSuggestion,
} from "../LocationAutocomplete";

interface LocationAutocompleteResponse<T> {
  context: {
    open: boolean;
    loading: boolean;
    shrink: boolean;
    value: T | null;
    search: string;
    options: T[];
    dialogProps: ReturnType<typeof useControlledDialog>;
  };
  handlers: {
    handleOnBlur: () => void;
    handleOnClose: () => void;
    handleOnFocus: () => void;
    handleOnInputChange: (
      _event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      newInputValue: string
    ) => void;
    handleOnOpen: () => void;
    handleOnChangeValue: (
      _event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      value: T
    ) => void;
    clearInput: () => void;
  };
}

export const useLocationAutocomplete = <T extends LocationSuggestion>({
  defaultValue,
  value: propValue,
  sortOptions,
  onChange,
  onOpen,
  onFocus,
  fetch,
  getOptionLabel,
  useDialogOnMobile,
}: LocationAutocompleteProps<T>): LocationAutocompleteResponse<T> => {
  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [shrink, setShrink] = useState<boolean>(false);

  const [value, setValue] = useState<T | null>(
    propValue ?? defaultValue ?? null
  );
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState<T[]>([]);

  const debouncedSearch = useDebounce(search, 500);

  const dialogProps = useControlledDialog();

  useEffect(() => {
    if (propValue != null && propValue.label !== value?.label)
      setValue(propValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propValue]);

  useEffect(() => {
    if (!open) return;

    if (value == null || getOptionLabel?.(value) !== debouncedSearch) {
      setLoading(true);
      setOptions([]);
      fetch(debouncedSearch, (newOptions: T[], fetchedSearch?: string) => {
        if (fetchedSearch == null || fetchedSearch === debouncedSearch) {
          setOptions(sortOptions ? sortOptions(newOptions) : newOptions);
          setLoading(false);
        }
      });
    }
  }, [fetch, open, debouncedSearch, sortOptions, value, getOptionLabel]);

  const handleOnInputChange = useCallback((_event, newInputValue) => {
    setSearch(newInputValue);
  }, []);

  const handleOnChangeValue = useCallback(
    (_event, value: T) => {
      if (useDialogOnMobile != null && useDialogOnMobile)
        dialogProps.closeDialog();

      setValue(value);
      onChange(value, {
        search,
        options,
      });
    },
    [dialogProps, onChange, useDialogOnMobile, search, options]
  );

  const handleOnOpen = useCallback(() => {
    onOpen?.();
    setOpen(true);
  }, [onOpen]);

  const handleOnClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleOnFocus = useCallback(() => {
    onFocus?.();
    setShrink(true);
    setOpen(true);
  }, [onFocus]);

  const handleOnBlur = useCallback(() => {
    setShrink(false);
    setOpen(false);
  }, []);

  const clearInput = useCallback(() => {
    setSearch("");
  }, []);

  return useMemo(() => {
    return {
      context: {
        open,
        loading,
        shrink,
        value,
        search,
        options,
        dialogProps,
      },
      handlers: {
        handleOnBlur,
        handleOnClose,
        handleOnFocus,
        handleOnInputChange,
        handleOnOpen,
        handleOnChangeValue,
        clearInput,
      },
    };
  }, [
    dialogProps,
    handleOnBlur,
    handleOnChangeValue,
    clearInput,
    handleOnClose,
    handleOnFocus,
    handleOnInputChange,
    handleOnOpen,
    loading,
    open,
    options,
    search,
    shrink,
    value,
  ]);
};
